/**
 * The fixture implementation of `Repo`.
 *
 * This is what the preview build runs on: no database, no network, no keys.
 * It exists to prove the seam holds — every screen already goes through the
 * interface, so `supabaseRepo` is a swap and not a rewrite.
 *
 * Writes mutate module-local copies. They survive navigation and die on
 * reload, which is the honest behaviour for a demo: nobody should mistake it
 * for saved work.
 */

import type {
  CatalogItem, CustomerSummary, JobDetail, JobSummary, Repo,
} from './repo.js';
import {
  FIXTURE_CANDIDATES, FIXTURE_CANNOT_ASSESS, FIXTURE_CUSTOMER, FIXTURE_EXCLUSIONS,
  FIXTURE_IMPACT, FIXTURE_PROFILE, FIXTURE_SHOTS,
} from '../fixtures/job.js';
import {
  ACCEPTANCE, CATALOG_PROMPTS, CATALOG_ROWS, CUSTOMERS, JOB_STATUSES, JOBS, WEEKS,
} from '../fixtures/shop.js';
import { extraShotsFor } from '../capture/shotlist.js';
import { currentStage, shotsTotalFor } from '../app/jobStage.js';
import type { CatalogEntry, ExclusionRule } from '../estimate/build.js';
import type { Verdict } from '../ai/review.js';
import type { RateProfile } from '../calc/types.js';

const jobs: JobSummary[] = JOBS.map((j) => ({ ...j }));
const customers: CustomerSummary[] = CUSTOMERS.map((c, i) => ({
  // The preview fixtures carry no contact details on purpose: an example
  // address here would be mailed to for real the first time someone wired
  // this build to a live SMTP server.
  id: `cust-${i + 1}`, email: null, phone: null, ...c,
}));
let catalog: CatalogItem[] = CATALOG_ROWS.map((e) => ({ ...e }));
const verdicts: Record<string, Record<string, Verdict>> = {};
const issued: Record<string, number> = {};
/** Operation overrides, mirroring the real table's two-column shape. */
const overrides: Record<string, Record<string, { proposed: string; operation: string }>> = {};

/** Not a security hash — a stable fingerprint so two totals cannot share one. */
function fingerprint(reference: string, version: number, total: number): string {
  const seed = `${reference}:${version}:${total}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

export const fixtureRepo: Repo = {
  async dashboardMetrics() {
    const count = (s: JobSummary['status']) => jobs.filter((j) => j.status === s).length;
    const staged = jobs.filter((j) => j.status === 'open').map((j) => ({
      reference: j.reference,
      customer: j.customer,
      stage: currentStage({
        shotsTaken: j.shotsTaken, shotsTotal: shotsTotalFor(j.impact),
        proposalsTotal: j.proposalsTotal, proposalsDecided: j.proposalsDecided,
      }),
      shotsLeft: Math.max(0, shotsTotalFor(j.impact) - j.shotsTaken),
      proposalsLeft: j.proposalsTotal - j.proposalsDecided,
    }));
    return {
      openJobs: count('open'),
      awaitingApproval: count('awaiting_approval'),
      invoicedThisMonth: jobs
        .filter((j) => j.status === 'invoiced')
        .reduce((sum, j) => sum + (j.total ?? 0), 0),
      weeks: [...WEEKS],
      acceptance: [...ACCEPTANCE],
      catalogPrompts: [...CATALOG_PROMPTS],
      byStatus: JOB_STATUSES.map((status) => ({ status, count: count(status) })),
      byStage: {
        capture: staged.filter((s) => s.stage === 'capture').length,
        review: staged.filter((s) => s.stage === 'review').length,
        price: staged.filter((s) => s.stage === 'price').length,
      },
      needsYou: staged.slice(0, 3).map((s) => ({
        reference: s.reference, customer: s.customer,
        reason: s.stage === 'capture'
          ? `${s.shotsLeft} photograph${s.shotsLeft === 1 ? '' : 's'} still needed`
          : s.stage === 'review'
            ? `${s.proposalsLeft} proposal${s.proposalsLeft === 1 ? '' : 's'} still need a verdict`
            : 'reviewed — ready for you to finish pricing',
      })),
    };
  },

  async createJob(input) {
    // Mirrors the real create_job RPC's own guard, so preview and tests
    // exercise the same rule production enforces: a brand new customer needs
    // an email, the same way it already needs a name.
    if (!input.customerId && !input.customerEmail.trim()) {
      throw new Error('createJob: a new customer needs an email address, to send the approval to');
    }
    // References count up from the highest already present, so the demo cannot
    // hand out a reference that already exists.
    const highest = jobs.reduce((max, j) => {
      const n = Number(j.reference.replace(/\D/g, ''));
      return Number.isFinite(n) && n > max ? n : max;
    }, 2400);
    const reference = `EST-${highest + 1}`;
    const customer = input.customerId
      ? customers.find((c) => c.id === input.customerId)
      : undefined;
    jobs.unshift({
      reference,
      customer: customer?.name ?? input.customerName,
      customerEmail: customer?.email ?? input.customerEmail,
      needsApproval: customer?.needsApproval ?? input.customerNeedsApproval,
      vehicle: [input.vehicle.year, input.vehicle.make, input.vehicle.model,
                input.vehicle.trim].filter(Boolean).join(' '),
      vin: input.vehicle.vin ?? null,
      impact: input.impact,
      status: 'open',
      total: null,
      shotsTaken: 0, proposalsTotal: 0, proposalsDecided: 0,
    });
    return { reference };
  },

  async listJobs() {
    return jobs.map((j) => ({ ...j }));
  },

  async updateJobDetails(reference, patch) {
    if (patch.customerEmail !== undefined && patch.customerEmail.trim() === '') {
      throw new Error('updateJobDetails: an email address is required');
    }
    const row = jobs.find((j) => j.reference === reference);
    if (!row) throw new Error(`updateJobDetails: no job ${reference}`);
    if (patch.vin !== undefined) row.vin = patch.vin?.trim() || null;
    if (patch.customerEmail !== undefined) row.customerEmail = patch.customerEmail.trim();
  },

  async getJob(reference) {
    const row = jobs.find((j) => j.reference === reference);
    if (!row) return null;
    // Only the worked example carries photographs and proposals. The rest are
    // list rows — which is exactly what a shop's history looks like.
    const worked = reference === JOBS[0]!.reference;
    return {
      // No database, so the reference stands in as the id. Nothing writes to a
      // real table in this mode, and the shape stays honest.
      id: reference,
      reference,
      customer: { ...FIXTURE_CUSTOMER, needsApproval: row.needsApproval },
      vehicle: row.vehicle,
      impact: FIXTURE_IMPACT,
      acv: 28400,
      shots: worked ? FIXTURE_SHOTS.map((s) => ({ ...s })) : [],
      extraShots: worked ? extraShotsFor(FIXTURE_CANNOT_ASSESS) : [],
      // No storage in the fixture build; Review falls back to the slot name.
      photoUrls: {},
      // The preview build keeps a draft only for as long as the tab is open.
      draft: {},
      candidates: worked ? FIXTURE_CANDIDATES.map((c) => ({ ...c })) : [],
      cannotAssess: worked ? FIXTURE_CANNOT_ASSESS.map((c) => ({ ...c })) : [],
      verdicts: { ...(verdicts[reference] ?? {}) },
      issued: null,
      approval: null,
      approvalSentTo: null,
      staleApproval: null,
    } satisfies JobDetail;
  },

  async listCustomers() {
    return customers.map((c) => ({ ...c }));
  },

  async setCustomerApproval(id, needsApproval) {
    const c = customers.find((x) => x.id === id);
    if (c) c.needsApproval = needsApproval;
  },

  async listCatalog() {
    return catalog.map((e) => ({ ...e }));
  },

  async addCatalogEntry(entry) {
    catalog = [
      ...catalog.filter((e) => !(e.partNumber === entry.partNumber
        && e.description === entry.description)),
      { ...entry, source: 'asked_once' },
    ];
  },

  async listExclusions(): Promise<ExclusionRule[]> {
    return FIXTURE_EXCLUSIONS.map((e) => ({ ...e }));
  },

  async getRateProfile(): Promise<RateProfile> {
    return { ...FIXTURE_PROFILE };
  },

  async recordVerdict(reference, proposalKey, verdict) {
    verdicts[reference] = { ...(verdicts[reference] ?? {}), [proposalKey]: verdict };
  },

  async recordOperation(reference, proposalKey, operation) {
    // Same idempotency rule as the real repo: first override captures the
    // model's answer, later ones must not clobber it.
    const forJob = overrides[reference] ?? (overrides[reference] = {});
    const seen = forJob[proposalKey];
    forJob[proposalKey] = { proposed: seen?.proposed ?? 'replace', operation };
  },

  async issue(reference, grandTotal, _lines, _totals, _currencyCode) {
    const version = (issued[reference] ?? 0) + 1;
    issued[reference] = version;
    const row = jobs.find((j) => j.reference === reference);
    if (row) { row.status = 'issued'; row.total = grandTotal; }
    return { version, hash: fingerprint(reference, version, grandTotal) };
  },

  async saveDraft(_reference: string, _draft: unknown) {
    // Nowhere to save to in the preview build.
  },

  async sendForApproval(_reference: string) {
    // No mail server in the preview build; say so rather than pretend.
    return { sentTo: 'nobody@example.test', link: '#', emailed: false };
  },

  async approvalStatus(_reference: string) {
    return null;
  },

  async createCustomer(input) {
    // Mirrors the real backend's own backstop, for the same reason.
    if (!input.email.trim()) throw new Error('createCustomer: an email address is required');
    // No database in the preview build; a stable fake id keeps callers happy.
    return { id: `fixture:${input.name}` };
  },

  async updateCustomer(_id: string, _patch: unknown) {
    // Nowhere to save to in the preview build.
  },

  async removeCatalogEntry(_key: unknown) {
    // Nothing persisted in the preview build.
  },

  async saveRateProfile(_profile: unknown) {
    // Nowhere to save to in the preview build.
  },

  async removePhoto(_reference: string, _slot: string) {
    // Nothing is uploaded in the preview build, so nothing to remove.
  },

  async askEstimate(_reference: string, _question: string) {
    // The preview build has no server and therefore no model. Saying so is
    // better than a canned answer that looks like a real one.
    return {
      answer: 'This is the preview build — it has no connection to a model, so I '
        + 'cannot read the estimate here. On a live shop this answers from the job '
        + 'in front of you.',
      refused: false,
    };
  },

  async analysePhotos(reference, provider) {
    const job = await fixtureRepo.getJob(reference);
    if (!job || job.shots.every((s) => s.status !== 'taken')) return null;
    // Preview has no model to call. The picker still works — it just always
    // "answers" with whichever one was chosen, so the UI can be exercised.
    return { added: job.candidates.length, provider: provider ?? 'gemini', model: 'preview' };
  },
};

/** Test seam: reset module state between cases. */
export function resetFixtureRepo(): void {
  catalog = CATALOG_ROWS.map((e) => ({ ...e }));
  for (const k of Object.keys(verdicts)) delete verdicts[k];
  for (const k of Object.keys(issued)) delete issued[k];
  JOBS.forEach((src, i) => { jobs[i] = { ...src }; });
  CUSTOMERS.forEach((src, i) => {
    customers[i] = { id: `cust-${i + 1}`, email: null, phone: null, ...src };
  });
}
